import { useEffect, useState } from 'react';
import { apiJson, searchCfAccounts } from '../auth/session.ts';
import { Icon } from './widgets.tsx';
import { useEditor } from './store.ts';

interface EditorUser {
  id: string;
  email: string | null;
  disabledAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface DirectoryAccount {
  id: string;
  email: string | null;
  providers: string[];
  created_at: string;
}

export function AccessDialog() {
  const close = () => useEditor.getState().set({ modal: null });
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DirectoryAccount[] | null>(null);
  const [users, setUsers] = useState<EditorUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = async () => {
    const body = await apiJson<{ users: EditorUser[] }>('GET', '/api/members');
    setUsers(body.users);
  };

  useEffect(() => {
    reload().catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load people'));
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      searchCfAccounts(q).then(setResults).catch((err: unknown) => {
        setResults([]);
        setError(err instanceof Error ? err.message : 'Search failed');
      });
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  const add = async (account: DirectoryAccount) => {
    if (!account.email) return;
    setBusy(account.id);
    setError(null);
    try {
      await apiJson('POST', '/api/members', { id: account.id, email: account.email });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add');
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (user: EditorUser) => {
    setBusy(user.id);
    setError(null);
    try {
      await apiJson('POST', `/api/members/${encodeURIComponent(user.id)}/${user.disabledAt ? 'enable' : 'disable'}`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update');
    } finally {
      setBusy(null);
    }
  };

  const enabled = new Set((users ?? []).filter((user) => !user.disabledAt).map((user) => user.id));

  return (
    <div className="modal-back" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>People</h2>
          <button className="icon-btn" onClick={close}><Icon name="close" /></button>
        </div>
        <div className="modal-body">
          <p className="muted">You can open the editor because you are a Cactus Fiesta admin. Add other accounts that should use it. They sign in with Google once so the account exists, then you can find them by email.</p>
          {error && <p className="warn">{error}</p>}
          <h3>Editor access</h3>
          {users === null && <div className="muted">Loading…</div>}
          {users?.length === 0 && <div className="muted">Nobody else has been added yet.</div>}
          <div className="account-list">
            {users?.map((user) => (
              <div key={user.id} className="account-row">
                <div className="who">
                  <b>{user.email ?? user.id}</b>
                  <span className="muted small">{user.disabledAt ? 'Access removed' : 'Can use the editor'}</span>
                </div>
                <button className="btn" disabled={busy === user.id} onClick={() => void toggle(user)}>
                  {user.disabledAt ? 'Restore' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
          <h3>Find an account</h3>
          <input className="text search" placeholder="Email" value={q} onChange={(e) => setQ(e.target.value)} />
          {results === null && <div className="muted">Searching…</div>}
          {results?.length === 0 && <div className="muted">No matching accounts.</div>}
          <div className="account-list">
            {results?.map((account) => (
              <div key={account.id} className="account-row">
                <div className="who">
                  <b>{account.email ?? account.id}</b>
                  <span className="muted small">{account.providers.join(', ') || 'no provider'}</span>
                </div>
                <button className="btn primary" disabled={!account.email || enabled.has(account.id) || busy === account.id} onClick={() => void add(account)}>
                  {enabled.has(account.id) ? 'Added' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
